import {useOnClickOutside} from 'usehooks-ts'
import {FaCaretUp} from 'react-icons/fa6'
import {useTranslations} from 'use-intl'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import clsx from 'clsx'
import {useBasketItemQuantityMutation, useBasketQuery} from '~/hooks/useBasket'
import BasketSkeletonLoader from '~/components/basket/BasketSkeletonLoader'
import {BasketItemList} from '~/components/basket/BasketItemList'
import {BasketError} from '~/components/basket/BasketErrorCard'
import {EmptyBasket} from '~/components/basket/EmptyBasketCard'
import BasketSummary from '~/components/basket/BasketSummary'
import {Button} from '~/components/common/Button'
import {Link} from '@tanstack/react-router'
import useLocaleParams from '~/hooks/useLocaleParams'

type BasketOverlayProps = {
    className?: string
    onClose: () => void
}

const BasketOverlay = ({className, onClose}: BasketOverlayProps) => {
    const t = useTranslations('basket')
    const params = useLocaleParams()
    const ref = useRef<HTMLDivElement>(null)
    const {data: basket, isLoading, isFetching, isError, error} = useBasketQuery()
    const {mutate: updateBasketItemQuantity, isPending} = useBasketItemQuantityMutation()

    const isBasketEmpty = !isLoading && !basket?.basketItems.length
    const isUpdatingBasket = isFetching || isPending

    useOnClickOutside(ref, (event) => {
        event.stopPropagation()
        onClose()
    })

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [onClose])

    // Focus trap
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== 'Tab' || !ref.current) return
            const focusable = ref.current.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
            if (focusable.length === 0) return
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault()
                first.focus()
            }
        },
        []
    )

    // Auto-focus first focusable element
    useEffect(() => {
        const el = ref.current?.querySelector<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled])'
        )
        el?.focus()
    }, [isLoading])

    const checkoutLink = useMemo(() => {
        const baseHref = basket?.links.find((link) => link.type === 'shopCheckout')?.href

        if (!baseHref) {
            return basket?.links[0]?.href ?? undefined
        }

        const basketUrl = new URL(baseHref)
        basketUrl.searchParams.set('continueShoppingLink', window.location.href)

        return basketUrl.toString()
    }, [basket?.links])

    return (
        <div className={clsx(className)}>
            <FaCaretUp className="fill-[#0e0e16] size-10 -mb-5 ml-auto mr-2" />
            <div
                className="w-96 max-h-[75svh] bg-[#0e0e16] backdrop-blur-xl transform-gpu rounded-md shadow flex"
                ref={ref}
                role="dialog"
                aria-modal="true"
                aria-label={t('title')}
                aria-busy={isLoading}
                aria-live="polite"
                onKeyDown={handleKeyDown}
            >
                <h2 className="sr-only">{t('title')}</h2>
                {isLoading && <BasketSkeletonLoader />}
                {!isLoading && (
                    <div className="p-4 flex flex-col overflow-hidden max-h-full">
                        {isError && <BasketError error={error} />}
                        {isBasketEmpty && <EmptyBasket />}

                        {!isBasketEmpty && basket && (
                            <>
                                <BasketItemList
                                    className="overflow-y-auto shrink"
                                    basketItems={basket.basketItems}
                                    onQuantityChange={updateBasketItemQuantity}
                                />
                                <div className="mt-2 border-t border-gray-600">
                                    <BasketSummary
                                        basket={basket}
                                        isRecalculating={isUpdatingBasket}
                                    />
                                    <div className="mt-6 space-y-3">
                                        <Button as={Link} to="/$lang/basket" params={params} onClick={onClose} className="w-full" variant="secondary">
                                            {t('viewCart')}
                                        </Button>
                                        <Button className="w-full" as="a" href={checkoutLink}>
                                            {t('checkout')}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default BasketOverlay
